import React, { createContext, useContext, useState, useCallback } from 'react'

const ToastContext = createContext()

export const useToast = () => {
  const context = useContext(ToastContext)
  if (!context) {
    throw new Error('useToast must be used within ToastProvider')
  }
  return context
}

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([])

  // Generate unique ID using crypto.randomUUID or fallback
  const generateId = () => {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      return crypto.randomUUID()
    }
    // Fallback: timestamp + random number
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
  }

  const showToast = useCallback((message, type = 'success', duration = 3000) => {
    const id = generateId()
    const toast = { id, message, type }
    setToasts((prev) => [...prev, toast])

    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id))
    }, duration)
  }, [])

  const success = useCallback((message, duration) => {
    showToast(message, 'success', duration)
  }, [showToast])

  const error = useCallback((message, duration) => {
    showToast(message, 'error', duration)
  }, [showToast])

  const info = useCallback((message, duration) => {
    showToast(message, 'info', duration)
  }, [showToast])

  const warning = useCallback((message, duration) => {
    showToast(message, 'warning', duration)
  }, [showToast])

  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id))
  }, [])

  return (
    <ToastContext.Provider value={{ success, error, info, warning, showToast }}>
      {children}
      <div className="fixed top-4 right-4 z-50 space-y-2">
        {toasts.map((toast) => (
          <div
            key={toast.id}
            className={`
              min-w-[300px] max-w-md px-4 py-3 rounded-lg shadow-lg
              flex items-center justify-between
              animate-in slide-in-from-top-5
              ${
                toast.type === 'success'
                  ? 'bg-emerald-500 text-white'
                  : toast.type === 'error'
                  ? 'bg-red-500 text-white'
                  : toast.type === 'warning'
                  ? 'bg-amber-500 text-white'
                  : toast.type === 'accent'
                  ? 'bg-[#cfa190] text-[#1e2a2e] font-semibold'
                  : 'bg-[#1e2a2e] text-white'
              }
            `}
          >
            <span className="flex items-center gap-2">
              {toast.type === 'success' && '✅'}
              {toast.type === 'error' && '❌'}
              {toast.type === 'info' && 'ℹ️'}
              {toast.type === 'warning' && '⚠️'}
              {toast.message}
            </span>
            <button
              onClick={() => removeToast(toast.id)}
              className={`ml-4 font-bold ${
                toast.type === 'accent' ? 'text-[#1e2a2e] hover:text-black' : 'text-white hover:text-gray-200'
              }`}
            >
              ×
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  )
}


































